import { useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Input,
  Modal,
  Progress,
  Spin,
  Statistic,
  Tag,
  Typography,
  message,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import cdrReviewApi, { CdrReviewApiError } from "@/services/cdrReviewApi";
import type { CdrCycleEvent, CdrCycleState } from "./clinicalReview.types";

type Props = {
  trialId: string;
};

const CADENCE_LABEL: Record<string, string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
};

function roleLabel(role: string): string {
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatMoment(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

function CycleEventRow({ event }: { event: CdrCycleEvent }) {
  const signedOff = event.action === "signed_off";
  const completion = event.completion as { percent_complete?: number };

  return (
    <div className="rounded-md border border-[#E6ECF1] px-3 py-2 text-xs">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-medium text-[#2B4752]">
          <Tag color={signedOff ? "green" : "gold"}>
            {signedOff ? "Signed off" : "Reopened"}
          </Tag>
          {event.cycle_seq !== undefined && (
            <span>Cycle {event.cycle_seq}</span>
          )}
        </span>
        <span className="text-[#8FA5A0]">{formatMoment(event.at)}</span>
      </div>
      <div className="mt-1 text-[#556C75]">
        {event.by || "Unknown"}
        {event.roles.length > 0 &&
          ` · ${event.roles.map(roleLabel).join(", ")}`}
      </div>
      {signedOff && completion?.percent_complete !== undefined && (
        <div className="text-[#556C75]">
          {completion.percent_complete}% dispositioned over data through{" "}
          {formatMoment(event.snapshot_at)}
        </div>
      )}
      {event.reason && (
        <div className="mt-1 text-[#2B4752]">{event.reason}</div>
      )}
    </div>
  );
}

export default function ClinicalReviewCycle({ trialId }: Props) {
  const queryClient = useQueryClient();
  const [reopening, setReopening] = useState<number | null>(null);
  const [reason, setReason] = useState("");

  const cycleQuery = useQuery({
    queryKey: ["cdr-cycle", trialId],
    queryFn: () => cdrReviewApi.cycle(trialId),
  });

  const action = useMutation({
    mutationFn: (body: {
      action: "sign_off" | "reopen";
      cycle_id?: number;
      reason?: string;
    }) => cdrReviewApi.cycleAction(trialId, body),
    onSuccess: (_result, variables) => {
      message.success(
        variables.action === "sign_off"
          ? "Cycle signed off."
          : "Cycle reopened.",
      );
      setReopening(null);
      setReason("");
      queryClient.invalidateQueries({ queryKey: ["cdr-cycle", trialId] });
    },
    onError: (error: unknown) => {
      const detail =
        error instanceof CdrReviewApiError
          ? error.message
          : "Could not complete that action.";
      message.error(detail);
    },
  });

  if (cycleQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spin />
      </div>
    );
  }

  const state = cycleQuery.data as CdrCycleState | undefined;
  if (!state) return <Empty description="Could not load the review cycle." />;

  const { cycle, completion, policy, can_sign_off: canSignOff } = state;
  const sealedEvents = state.events.filter((e) => e.action === "signed_off");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-6 rounded-md border border-[#E6ECF1] p-4">
        <Statistic
          title={`Cycle ${cycle.seq}`}
          value={completion.percent_complete}
          suffix="%"
          precision={1}
        />
        <div className="min-w-[220px] flex-1">
          <Progress
            percent={completion.percent_complete}
            status={cycle.status === "sealed" ? "success" : "active"}
          />
          <Typography.Text type="secondary" className="text-xs">
            {completion.dispositioned} of {completion.in_scope} records
            dispositioned · {completion.outstanding} outstanding
          </Typography.Text>
        </div>

        {cycle.status === "sealed" ? (
          <Tag color="green">Signed off {formatMoment(cycle.sealed_at)}</Tag>
        ) : (
          <Button
            type="primary"
            disabled={!canSignOff}
            loading={
              action.isPending && action.variables?.action === "sign_off"
            }
            onClick={() => action.mutate({ action: "sign_off" })}
          >
            Sign off cycle {cycle.seq}
          </Button>
        )}
      </div>

      {completion.outstanding > 0 && cycle.status !== "sealed" && (
        <Alert
          type="warning"
          showIcon
          message={`${completion.outstanding} records are not yet dispositioned.`}
          description="A cycle can still be signed off with records outstanding — the shortfall is recorded on the sign-off."
        />
      )}

      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Cadence">
          {CADENCE_LABEL[policy.cadence] ?? policy.cadence}
        </Descriptions.Item>
        <Descriptions.Item label="Sign-off role">
          {roleLabel(policy.signoff_role)}
          <Typography.Text type="secondary" className="ml-2 text-xs">
            ({policy.source})
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Opened">
          {formatMoment(cycle.opened_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Data received through">
          {formatMoment(cycle.watermark_at)}
        </Descriptions.Item>
      </Descriptions>

      <section className="flex flex-col gap-2">
        <Typography.Text strong>Cycle history</Typography.Text>
        {state.events.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No cycle has been signed off yet."
          />
        ) : (
          <div className="flex flex-col gap-2">
            {state.events.map((event) => (
              <CycleEventRow key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>

      {canSignOff && sealedEvents.length > 0 && (
        <div>
          <Button
            size="small"
            onClick={() => setReopening(sealedEvents[0].id)}
            disabled={cycle.status !== "sealed"}
          >
            Reopen cycle {cycle.seq}
          </Button>
        </div>
      )}

      <Modal
        title={`Reopen cycle ${cycle.seq}`}
        open={reopening !== null}
        okText="Reopen"
        okButtonProps={{ disabled: reason.trim().length === 0 }}
        confirmLoading={action.isPending}
        onCancel={() => {
          setReopening(null);
          setReason("");
        }}
        onOk={() =>
          action.mutate({
            action: "reopen",
            cycle_id: cycle.id,
            reason: reason.trim(),
          })
        }
      >
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message="The existing sign-off stays on the record."
          description="Reopening is recorded as a new event, not an edit — it remains true that this cycle was signed off over that snapshot."
        />
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why is this cycle being reopened?"
          aria-label="Reason for reopening"
        />
      </Modal>
    </div>
  );
}
